// src/pages/LiveFeed.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import BBSHeader from '../components/BBS/BBSHeader';
import BBSNav from '../components/BBS/BBSNav';
import StatusBar from '../components/BBS/StatusBar';
import { trackEvent } from '../utils/analytics';

// ============================================
// VIDEO CLIP REGISTRY
// Put your .mp4 files in public/videos/
// ============================================
const VIDEO_CLIPS = [
  'clip1.mp4',
  'clip2.mp4',
  'clip3.mp4',
  'clip4.mp4',
  'clip5.mp4',
  'clip6.mp4',
  'clip7.mp4',
  'clip8.mp4',
  'clip9.mp4',
  'clip10.mp4',
  'clip11.mp4',
  'clip12.mp4',
  'clip13.mp4',
  'clip14.mp4',
  'clip15.mp4',
  'clip16.mp4',
  'clip17.mp4',
  'clip18.mp4',
  'clip19.mp4',
];

const INTERFERENCE_MESSAGES = [
  'FEED INTERRUPTED — RECONNECTING...',
  'UNAUTHORIZED SIGNAL DETECTED...',
  'SWITCHING TO BACKUP FEED...',
  'WARNING: UNRECOGNIZED BROADCAST SOURCE',
  'SIGNAL DEGRADATION — COMPENSATING...',
  'UNKNOWN ORIGIN — TRACING...',
  'DECRYPTING FEED...',
];

const CHANNEL_LABELS = [
  'CAM-15 // UNKNOWN',
  'CAM-██ // [REDACTED]',
  'SAT-02 // ORBITAL',
  'REC-09 // ARCHIVE PLAYBACK',
  'CAM-03 // SUBLEVEL',
  'MOB-██ // INTERCEPTED',
  'SAT-07 // DEEP FIELD',
  'REC-22 // RECOVERED',
];

export default function LiveFeed() {
  const [currentClipIndex, setCurrentClipIndex] = useState(-1);
  const [isStatic, setIsStatic] = useState(true);
  const [interferenceMsg, setInterferenceMsg] = useState('INITIALIZING FEED...');
  const [channelLabel, setChannelLabel] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [clipHistory, setClipHistory] = useState([]);
  const videoRef = useRef(null);
  const staticCanvasRef = useRef(null);
  const staticAnimRef = useRef(null);
  const timestampRef = useRef(null);

  // Generate fake timestamp
  const updateTimestamp = useCallback(() => {
    const now = new Date();
    const fakeYear = 1987 + Math.floor(Math.random() * 10);
    const ts = `${fakeYear}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    setTimestamp(ts);
  }, []);

  useEffect(() => {
    updateTimestamp();
    timestampRef.current = setInterval(updateTimestamp, 1000);
    return () => clearInterval(timestampRef.current);
  }, [updateTimestamp]);

  // Draw TV static on canvas
  useEffect(() => {
    const canvas = staticCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    function drawStatic() {
      const w = canvas.width;
      const h = canvas.height;
      const imageData = ctx.createImageData(w, h);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const val = Math.random() * 255;
        data[i] = val;
        data[i + 1] = val;
        data[i + 2] = val;
        data[i + 3] = 255;
      }

      ctx.putImageData(imageData, 0, 0);

      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      for (let y = 0; y < h; y += 3) {
        ctx.fillRect(0, y, w, 1);
      }

      staticAnimRef.current = requestAnimationFrame(drawStatic);
    }

    if (isStatic) {
      drawStatic();
    }

    return () => {
      if (staticAnimRef.current) {
        cancelAnimationFrame(staticAnimRef.current);
      }
    };
  }, [isStatic]);

  const pickRandomClip = useCallback(() => {
    if (VIDEO_CLIPS.length === 0) return -1;
    if (VIDEO_CLIPS.length === 1) return 0;

    let next;
    do {
      next = Math.floor(Math.random() * VIDEO_CLIPS.length);
    } while (next === currentClipIndex);

    return next;
  }, [currentClipIndex]);

  const playNextClip = useCallback(() => {
    setIsStatic(true);
    setInterferenceMsg(
      INTERFERENCE_MESSAGES[Math.floor(Math.random() * INTERFERENCE_MESSAGES.length)]
    );

    const staticDuration = 1000 + Math.random() * 3000;

    setTimeout(() => {
      const nextIndex = pickRandomClip();
      if (nextIndex === -1) return;

      setCurrentClipIndex(nextIndex);
      setChannelLabel(
        CHANNEL_LABELS[Math.floor(Math.random() * CHANNEL_LABELS.length)]
      );
      setIsStatic(false);
      setClipHistory(prev => [...prev.slice(-20), VIDEO_CLIPS[nextIndex]]);

      trackEvent('live_feed_clip', 'engagement', VIDEO_CLIPS[nextIndex]);
    }, staticDuration);
  }, [pickRandomClip]);

  const handleClipEnd = useCallback(() => {
    playNextClip();
  }, [playNextClip]);

  function toggleFeed() {
    if (isPlaying) {
      setIsPlaying(false);
      setIsStatic(true);
      setInterferenceMsg('FEED TERMINATED BY OPERATOR');
      if (videoRef.current) {
        videoRef.current.pause();
      }
    } else {
      setIsPlaying(true);
      trackEvent('live_feed_start', 'engagement', 'feed_activated');
      playNextClip();
    }
  }

  useEffect(() => {
    if (!isStatic && videoRef.current && currentClipIndex >= 0) {
      videoRef.current.load();
      videoRef.current.play().catch(err => {
        console.log('Autoplay blocked, showing static');
        setIsStatic(true);
        setInterferenceMsg('AUTOPLAY BLOCKED — CLICK TO RESUME');
      });
    }
  }, [isStatic, currentClipIndex]);

  return (
    <div>
      <BBSHeader title="LIVE FEED" subtitle="SURVEILLANCE MONITORING SYSTEM" />
      <BBSNav />

      <div style={{
        border: '2px solid #333',
        background: '#000',
        maxWidth: '480px',
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
      }}>

        {/* VHS-style top bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px 12px',
          background: 'rgba(0,0,0,0.9)',
          borderBottom: '1px solid #222',
          fontSize: '0.75rem',
          fontFamily: "'VT323', 'Courier New', monospace",
          zIndex: 5,
          position: 'relative',
        }}>
          <span style={{ color: isPlaying ? '#ff0040' : '#555' }}>
            {isPlaying ? '● REC' : '○ STANDBY'}
          </span>
          <span style={{ color: '#555' }}>
            {channelLabel || 'NO SIGNAL'}
          </span>
          <span style={{ color: '#666' }}>
            {timestamp}
          </span>
        </div>

        {/* Video / Static display area — 9:16 portrait, capped on desktop */}
        <div style={{
            position: 'relative',
            aspectRatio: '9 / 16',
            maxHeight: '70vh',
            maxWidth: '100%',
            background: '#000',
            overflow: 'hidden',
            margin: '0 auto',
          }}>

          {/* Static canvas */}
          <canvas
            ref={staticCanvasRef}
            width={270}
            height={480}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: isStatic ? 'block' : 'none',
              imageRendering: 'pixelated',
            }}
          />

          {/* Interference message overlay */}
          {isStatic && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#ff0040',
              fontFamily: "'VT323', 'Courier New', monospace",
              fontSize: 'clamp(0.8rem, 3vw, 1.2rem)',
              textAlign: 'center',
              textShadow: '0 0 10px rgba(255, 0, 64, 0.8)',
              zIndex: 2,
              animation: 'pulse-red 2s ease-in-out infinite',
              padding: '0 20px',
            }}>
              {interferenceMsg}
            </div>
          )}

          {/* Video element */}
          <video
            ref={videoRef}
            onEnded={handleClipEnd}
            muted
            playsInline
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: isStatic ? 'none' : 'block',
              filter: 'contrast(1.2) brightness(0.9) saturate(0.6)',
            }}
          >
            {currentClipIndex >= 0 && (
              <source
                src={`/videos/${VIDEO_CLIPS[currentClipIndex]}`}
                type="video/mp4"
              />
            )}
          </video>

          {/* VHS overlay effects */}
          {!isStatic && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 3,
              background: `
                repeating-linear-gradient(
                  0deg,
                  rgba(0, 0, 0, 0.15) 0px,
                  rgba(0, 0, 0, 0.15) 1px,
                  transparent 1px,
                  transparent 3px
                )
              `,
            }}>
              <div style={{
                position: 'absolute',
                left: 0,
                width: '100%',
                height: '3px',
                background: 'rgba(255, 255, 255, 0.1)',
                animation: 'vhs-tracking 8s linear infinite',
              }} />
            </div>
          )}

          {/* Channel label on video */}
          {!isStatic && channelLabel && (
            <div style={{
              position: 'absolute',
              bottom: '12px',
              left: '12px',
              color: '#fff',
              fontFamily: "'VT323', 'Courier New', monospace",
              fontSize: '0.85rem',
              textShadow: '1px 1px 2px #000, 0 0 8px rgba(255,255,255,0.3)',
              zIndex: 4,
              opacity: 0.8,
            }}>
              {channelLabel}
            </div>
          )}
        </div>

        {/* Bottom control bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          background: 'rgba(0,0,0,0.9)',
          borderTop: '1px solid #222',
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          <button
            onClick={toggleFeed}
            className={isPlaying ? 'bbs-button bbs-button-danger' : 'bbs-button'}
            style={{ padding: '6px 20px', fontSize: '0.85rem' }}
          >
            {isPlaying ? '■ DISCONNECT' : '▶ CONNECT TO FEED'}
          </button>

          {isPlaying && (
            <button
              onClick={playNextClip}
              className="bbs-button"
              style={{ padding: '6px 16px', fontSize: '0.85rem' }}
            >
              ▶▶ NEXT CHANNEL
            </button>
          )}

          <span style={{
            color: '#333',
            fontFamily: "'VT323', monospace",
            fontSize: '0.75rem',
          }}>
            {clipHistory.length > 0 ? `${clipHistory.length} SEGMENTS VIEWED` : ''}
          </span>
        </div>
      </div>

      {/* Description text below the feed */}
      <div style={{
        maxWidth: '480px',
        margin: '20px auto',
        padding: '15px',
        border: '1px solid #222',
        color: '#555',
        fontSize: '0.85rem',
        fontFamily: "'VT323', 'Courier New', monospace",
        lineHeight: '1.6',
      }}>
        <div style={{ color: '#ff0040', marginBottom: '8px' }}>
          ▓ NOTICE ▓
        </div>
        This feed aggregates surveillance footage from dark web sources we cannot verify.
        Broadcast timestamps do not correspond to any recognized standard. 
        Viewer discretion is advised.
        <br /><br />
        <span style={{ color: '#333' }}>
          We are not responsible for what you see.
        </span>
      </div>

      <StatusBar />
    </div>
  );
}